/*
 * Benvel Quick View modal controller — Story 15.1 (scaffold) +
 *                                      Story 15.2 (state matrix).
 *
 * Singleton <benvel-quick-view-dialog> appended to <body> (or already
 * present if `snippets/quick-view-modal.liquid` is rendered server-side
 * from theme.liquid — both paths supported).
 *
 * Delegated click handler upgrades any `[data-bv-quick-view-product-url]`
 * anchor click to:
 *   1. event.preventDefault()
 *   2. Fetch `${productUrl}?section_id=main-product` via Section Rendering
 *      API (Decision §D3 Path B).
 *   3. Project the canonical sub-sections (gallery / variant picker /
 *      price / fit / ATC) into `#bv-quick-view-content`.
 *   4. Apply Liquid-time-computable states (sold-out, no-size) from the
 *      SRA product JSON (Story 15.2 state matrix).
 *   5. dialog.showModal()
 *   6. Focus first interactive element in the dialog.
 *
 * Story 15.2 state matrix (AC1 D1 — deterministic precedence):
 *   network-error > sold-out > variant-unavailable > no-size > default
 *
 *   network-error    — SRA fetch failed (status != 2xx or network exception).
 *                      Retry via the same fetch path (D3). In-flight guard.
 *                      After 2nd failure, PDP fallback link revealed (D7).
 *   sold-out         — all product.variants[].available == false.
 *                      Shows BackInStockCTA stub (Story 14.5 canonical not
 *                      yet in Epic 15 scope at ship time — stub hydrated with
 *                      variant ID + product data).
 *   variant-unavailable — buyer selected a combination whose variant.available
 *                      == false (or inventory_quantity == 0 with
 *                      inventory_policy == 'deny'). Shows inline message;
 *                      ATC disabled (honest-stock FR9/NFR23 per Story 4.4).
 *   no-size          — product has no Size/Talla/Tamaño option axis.
 *                      Variant picker collapses; ATC remains visible+clickable.
 *   default          — product has size, ≥1 variant buyable, fetch succeeded.
 *
 * No-JS / network-error fallback (Story 15.1):
 *   - Anchor's default href is `product.url` — click without JS navigates
 *     to PDP (AC2).
 *   - On SRA double-failure, PDP fallback link revealed in network-error state.
 *
 * Close paths (AC4):
 *   - ESC keypress — native <dialog> behavior, no custom handler needed.
 *   - Scrim click — explicit listener (native <dialog> does NOT close on
 *     backdrop click by default).
 *   - Close button — declarative `on:click="/closeDialog"` (Horizon
 *     pattern) OR JS dialog.close() fallback.
 *
 * Focus trap (AC5):
 *   - Native <dialog>.showModal() in modern browsers already traps focus.
 *   - JS explicitly moves initial focus to the first interactive descendant
 *     of #bv-quick-view-content (or the close button as fallback).
 *   - On state transition (e.g. network-error → default after retry),
 *     focus returns to the variant picker's first interactive option (AC4).
 *   - Browser-native: focus returns to the triggering element on close.
 *
 * Analytics (AC4 + sprint change proposal §3 Q6 #3 + Story 15.2 AC2):
 *   `benvel:quick-view-closed` — dispatched ONLY when consent granted.
 *   `benvel:quick-view-network-error` — dispatched ONLY when consent
 *     granted (Story 8.1 kernel pattern; opaque-IDs only — NO PII).
 *
 * Asset budget target: ≤ 5KB gzipped (Story 1.9 / 1.10 Tier 1).
 */

(function () {
  'use strict';

  // ──────────────────────────────────────────────────────────────────────────
  // Constants
  // ──────────────────────────────────────────────────────────────────────────

  var DIALOG_HOST_SELECTOR = 'benvel-quick-view-dialog';
  var TRIGGER_SELECTOR = '[data-bv-quick-view-product-url]';
  var CONTENT_SLOT_SELECTOR = '[data-bv-quick-view-content]';
  var SECTION_ID = 'main-product';

  var EVENT_QV_CLOSED = 'benvel:quick-view-closed';
  var EVENT_QV_NETWORK_ERROR = 'benvel:quick-view-network-error'; // Story 15.2 AC2

  // Size option names to detect the no-size state (Story 15.2 AC1).
  var SIZE_OPTION_NAMES = ['size', 'talla', 'tamaño'];

  // Selectors used to project the SRA-fetched main-product section into
  // the modal slot. Order matters — modal layout is composed in this
  // sequence (gallery, header, picker, price, fit, ATC).
  var PROJECT_SELECTORS = [
    '[data-pdp-gallery]',
    '.pdp-gallery',
    '.product-media-gallery',
    'product-media-gallery',
    '[data-pdp-variant-picker]',
    '.pdp-variant-picker',
    'variant-picker-component',
    'benvel-variant-picker',
    '[data-pdp-price-caption]',
    '.pdp-price-caption',
    '[data-pdp-fit-block]',
    '.pdp-fit-block',
    'add-to-cart-component',
    '.add-to-cart-button',
  ];

  var FOCUSABLE_SELECTOR =
    'button:not([disabled]),' +
    'select:not([disabled]),' +
    'input:not([disabled]):not([type="hidden"]),' +
    'textarea:not([disabled]),' +
    'a[href],' +
    '[tabindex]:not([tabindex="-1"])';

  // ──────────────────────────────────────────────────────────────────────────
  // Singleton state
  // ──────────────────────────────────────────────────────────────────────────

  var dialogEl = null;   // <dialog>
  var hostEl = null;     // <benvel-quick-view-dialog>
  var contentEl = null;  // #bv-quick-view-content
  var liveEl = null;     // #bv-quick-view-live (aria-live region)
  var lastTriggerEl = null;
  var currentProductUrl = null;
  var isOpen = false;

  // Story 15.2 — in-flight guard for retry (D3: single outstanding request).
  var fetchInFlight = false;
  var retryCount = 0;
  var MAX_RETRIES = 1; // after the initial attempt + 1 retry → reveal PDP link

  // ──────────────────────────────────────────────────────────────────────────
  // Consent helper (Story 8.1 kernel)
  // ──────────────────────────────────────────────────────────────────────────

  function hasAnalyticsConsent() {
    return (
      typeof window !== 'undefined' &&
      window.benvel &&
      window.benvel.consent &&
      typeof window.benvel.consent.has === 'function' &&
      window.benvel.consent.has('analytics') === true
    );
  }

  function emitConsentGated(eventName, detail) {
    if (!hasAnalyticsConsent()) return;
    try {
      document.dispatchEvent(new CustomEvent(eventName, { detail: detail }));
    } catch (_err) {
      /* analytics never throws */
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Helpers
  // ──────────────────────────────────────────────────────────────────────────

  function ensureSingleton() {
    if (dialogEl) return;
    hostEl = document.querySelector(DIALOG_HOST_SELECTOR);
    if (!hostEl) return;
    dialogEl = hostEl.querySelector('dialog');
    contentEl = hostEl.querySelector(CONTENT_SLOT_SELECTOR);
    liveEl = hostEl.querySelector('#bv-quick-view-live');
    if (!dialogEl || !contentEl) return;

    // Scrim close (AC4) — native <dialog> doesn't close on backdrop click.
    dialogEl.addEventListener('click', function (e) {
      if (e.target === dialogEl) dialogEl.close();
    });

    // Close-button explicit fallback.
    var closeBtn = dialogEl.querySelector('.quick-view-modal__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        if (typeof dialogEl.close === 'function') dialogEl.close();
      });
    }

    // Retry button — Story 15.2 AC2.
    var retryBtn = dialogEl.querySelector('[data-bv-qv-retry]');
    if (retryBtn) {
      retryBtn.addEventListener('click', function () {
        if (fetchInFlight) return; // in-flight guard
        if (currentProductUrl) {
          retryFetch(currentProductUrl);
        }
      });
    }

    // Emit consent-gated analytics on close.
    dialogEl.addEventListener('close', function () {
      isOpen = false;
      clearState();
      emitConsentGated(EVENT_QV_CLOSED, {
        productId: lastTriggerEl
          ? lastTriggerEl.getAttribute('data-bv-quick-view-product-id') || null
          : null,
        sourcePage: lastTriggerEl
          ? lastTriggerEl.getAttribute('data-bv-source-page') || 'plp'
          : 'plp',
      });
      // Native <dialog> returns focus to lastTriggerEl automatically;
      // verify and fall back if browser support gap.
      if (lastTriggerEl && document.activeElement === document.body) {
        try { lastTriggerEl.focus(); } catch (_err) { /* no-op */ }
      }
    });

    // Listen for variant-changed events from the SRA-injected variant picker.
    // Story 15.2 AC1: detect variant-unavailable state at runtime.
    dialogEl.addEventListener('benvel:variant-changed', onVariantChanged);
  }

  // ──────────────────────────────────────────────────────────────────────────
  // State machine (Story 15.2 AC1 — 5 mutually-exclusive states)
  // ──────────────────────────────────────────────────────────────────────────

  // State precedence (D1): network-error > sold-out > variant-unavailable
  //                         > no-size > default

  function setDialogState(state) {
    if (!dialogEl) return;
    if (state) {
      dialogEl.setAttribute('data-quick-view-state', state);
    } else {
      dialogEl.removeAttribute('data-quick-view-state');
    }

    // Show/hide edge-state branches.
    var branches = dialogEl.querySelectorAll('[data-bv-qv-state]');
    for (var i = 0; i < branches.length; i++) {
      var branch = branches[i];
      if (branch.getAttribute('data-bv-qv-state') === state) {
        branch.removeAttribute('hidden');
      } else {
        branch.setAttribute('hidden', '');
      }
    }

    // Announce to screen readers via aria-live region.
    if (liveEl) {
      var msg = '';
      if (state === 'variant-unavailable') {
        var unavailMsg = dialogEl.querySelector('[data-bv-qv-unavail-message]');
        if (unavailMsg) msg = unavailMsg.textContent;
      } else if (state === 'network-error') {
        var errMsg = dialogEl.querySelector('[data-bv-qv-network-error-message]');
        if (errMsg) msg = errMsg.textContent;
      }
      liveEl.textContent = msg;
    }
  }

  function clearState() {
    setDialogState(null);
    retryCount = 0;
    fetchInFlight = false;
    currentProductUrl = null;
    // Re-hide PDP fallback link.
    if (dialogEl) {
      var fallback = dialogEl.querySelector('[data-bv-qv-pdp-fallback]');
      if (fallback) fallback.setAttribute('hidden', '');
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Product data analysis (sold-out, no-size — Story 15.2 AC1)
  // ──────────────────────────────────────────────────────────────────────────

  function hasSizeAxis(productJson) {
    var options = productJson && productJson.options;
    if (!Array.isArray(options)) return false;
    for (var i = 0; i < options.length; i++) {
      var name = (options[i] || '').toLowerCase();
      if (SIZE_OPTION_NAMES.indexOf(name) >= 0) return true;
    }
    return false;
  }

  function isSoldOut(productJson) {
    var variants = productJson && productJson.variants;
    if (!Array.isArray(variants) || variants.length === 0) return false;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i] && variants[i].available) return false;
    }
    return true;
  }

  function detectProductState(productJson) {
    // Precedence: sold-out > no-size > default
    // (network-error and variant-unavailable are detected separately)
    if (isSoldOut(productJson)) return 'sold-out';
    if (!hasSizeAxis(productJson)) return 'no-size';
    return null; // default
  }

  // Hydrate the sold-out stub CTA with product data from the SRA response.
  function hydrateSoldOutStub(productJson) {
    if (!dialogEl || !productJson) return;
    var stub = dialogEl.querySelector('[data-quick-view-back-in-stock-stub]');
    if (!stub) return;
    var notifyBtn = stub.querySelector('[data-bv-bis-trigger]');
    if (notifyBtn && productJson.variants && productJson.variants[0]) {
      notifyBtn.setAttribute('data-bv-bis-trigger', String(productJson.variants[0].id || ''));
      notifyBtn.setAttribute('data-bv-bis-product-handle', productJson.handle || '');
      notifyBtn.setAttribute('data-bv-bis-product-title', productJson.title || '');
    }
  }

  // Fetch product JSON to detect state. Uses the /products/<handle>.js endpoint.
  function fetchProductJson(productUrl, callback) {
    var handle = productUrl.replace(/\?.*$/, '').replace(/\/$/, '').split('/').pop();
    fetch('/products/' + handle + '.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('product json fetch failed: ' + res.status);
        return res.json();
      })
      .then(function (json) { callback(null, json); })
      .catch(function (err) { callback(err, null); });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Variant-unavailable detection (Story 15.2 AC1 + AC4)
  // ──────────────────────────────────────────────────────────────────────────

  function onVariantChanged(e) {
    if (!e.detail) return;
    var available = e.detail.available;
    var soldOut = e.detail.soldOut;

    // If sold-out state is already set (all variants OOS), skip variant-
    // unavailable detection — state precedence holds.
    var currentState = dialogEl ? dialogEl.getAttribute('data-quick-view-state') : null;
    if (currentState === 'sold-out' || currentState === 'network-error') return;

    if (soldOut || available === false) {
      setDialogState('variant-unavailable');
      // Disable the ATC button (honest-stock FR9/NFR23 per Story 4.4).
      disableAtcButton(true);
      // Wire aria-describedby from ATC button to the unavailable message.
      wireAriaDescribedby('bv-qv-unavail-msg');
    } else {
      // Variant is available — clear variant-unavailable state if that's
      // what's set; leave other states intact.
      if (currentState === 'variant-unavailable' || currentState === 'no-size' || !currentState) {
        setDialogState(null);
        disableAtcButton(false);
        // Return focus to the variant picker's first interactive option (AC4).
        refocusVariantPicker();
      }
    }
  }

  function disableAtcButton(disable) {
    if (!contentEl) return;
    var atcBtns = contentEl.querySelectorAll('add-to-cart-component button, .add-to-cart-button button, [type="submit"]');
    for (var i = 0; i < atcBtns.length; i++) {
      if (disable) {
        atcBtns[i].setAttribute('disabled', '');
        atcBtns[i].setAttribute('aria-disabled', 'true');
      } else {
        atcBtns[i].removeAttribute('disabled');
        atcBtns[i].removeAttribute('aria-disabled');
      }
    }
  }

  function wireAriaDescribedby(msgId) {
    if (!contentEl) return;
    var atcBtns = contentEl.querySelectorAll('add-to-cart-component button, .add-to-cart-button button, [type="submit"]');
    for (var i = 0; i < atcBtns.length; i++) {
      atcBtns[i].setAttribute('aria-describedby', msgId);
    }
  }

  function refocusVariantPicker() {
    if (!contentEl) return;
    // After state clears, focus the first interactive in the variant picker.
    var picker = contentEl.querySelector('benvel-variant-picker, [data-pdp-variant-picker], .pdp-variant-picker');
    if (picker) {
      var first = picker.querySelector(FOCUSABLE_SELECTOR);
      if (first) { try { first.focus(); } catch (_e) { /* no-op */ } }
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // No-size state — collapse variant picker region
  // ──────────────────────────────────────────────────────────────────────────

  function applyNoSizeState() {
    if (!contentEl) return;
    // Collapse the variant picker area — aria-hidden + hidden attribute.
    var picker = contentEl.querySelector('benvel-variant-picker, [data-pdp-variant-picker], .pdp-variant-picker');
    if (picker) {
      picker.setAttribute('hidden', '');
      picker.setAttribute('aria-hidden', 'true');
    }
    // ATC remains visible and clickable (no changes to ATC).
    setDialogState('no-size');
  }

  // ──────────────────────────────────────────────────────────────────────────
  // SRA projection
  // ──────────────────────────────────────────────────────────────────────────

  function projectSectionInto(slotEl, sectionDom) {
    while (slotEl.firstChild) slotEl.removeChild(slotEl.firstChild);
    var seen = new WeakSet();
    PROJECT_SELECTORS.forEach(function (sel) {
      var nodes = sectionDom.querySelectorAll(sel);
      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];
        if (seen.has(node)) continue;
        var skip = false;
        var p = node.parentElement;
        while (p && p !== sectionDom) {
          if (seen.has(p)) { skip = true; break; }
          p = p.parentElement;
        }
        if (skip) continue;
        seen.add(node);
        slotEl.appendChild(node.cloneNode(true));
      }
    });

    // Permanent "view full details" anchor — escape hatch to PDP (AC3/Story15.1).
    var fullLink = document.createElement('a');
    fullLink.className = 'quick-view-modal__view-full';
    fullLink.href = slotEl.getAttribute('data-bv-quick-view-product-url') || currentProductUrl || '#';
    fullLink.textContent = (window.benvel && window.benvel.i18n && window.benvel.i18n.quickViewViewFull) || 'Ver detalles completos';
    slotEl.appendChild(fullLink);
  }

  function focusFirstInteractive(slotEl) {
    var first = slotEl.querySelector(FOCUSABLE_SELECTOR);
    if (first) {
      try { first.focus(); return; } catch (_err) { /* fallthrough */ }
    }
    var closeBtn = dialogEl && dialogEl.querySelector('.quick-view-modal__close');
    if (closeBtn) {
      try { closeBtn.focus(); } catch (_err) { /* no-op */ }
    }
  }

  function fallbackNavigate(productUrl) {
    if (productUrl) window.location.assign(productUrl);
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Network-error state + retry (Story 15.2 AC2)
  // ──────────────────────────────────────────────────────────────────────────

  function showNetworkError(productUrl) {
    // Update PDP fallback link href.
    if (dialogEl) {
      var fallback = dialogEl.querySelector('[data-bv-qv-pdp-fallback]');
      if (fallback) {
        fallback.setAttribute('href', productUrl || '#');
        // Reveal fallback link after second failure (D7).
        if (retryCount >= MAX_RETRIES) {
          fallback.removeAttribute('hidden');
        }
      }
    }
    setDialogState('network-error');

    // Consent-gated analytics (Story 15.2 AC2 D5).
    emitConsentGated(EVENT_QV_NETWORK_ERROR, {
      productUrl: productUrl,
      retryCount: retryCount,
      sourcePage: lastTriggerEl
        ? lastTriggerEl.getAttribute('data-bv-source-page') || 'plp'
        : 'plp',
    });

    // Focus the retry button so keyboard users can act immediately.
    if (dialogEl) {
      var retryBtn = dialogEl.querySelector('[data-bv-qv-retry]');
      if (retryBtn) {
        try { retryBtn.focus(); } catch (_e) { /* no-op */ }
      }
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Open / close / retry
  // ──────────────────────────────────────────────────────────────────────────

  function performFetch(productUrl, isRetry) {
    if (fetchInFlight) return;
    fetchInFlight = true;

    var fetchUrl =
      productUrl + (productUrl.indexOf('?') >= 0 ? '&' : '?') +
      'section_id=' + encodeURIComponent(SECTION_ID);

    fetch(fetchUrl, { credentials: 'same-origin', headers: { 'Accept': 'text/html' } })
      .then(function (res) {
        if (!res.ok) throw new Error('SRA fetch failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        fetchInFlight = false;
        retryCount = 0;

        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        projectSectionInto(contentEl, doc.body);

        // Show the dialog first, then apply state (so DOM mutations are live).
        if (!isOpen) {
          if (typeof dialogEl.showModal === 'function') {
            dialogEl.showModal();
            isOpen = true;
          } else {
            // No <dialog> support — graceful PDP navigation.
            fallbackNavigate(productUrl);
            return;
          }
        } else {
          // Retry path: dialog already open, clear network-error state.
          setDialogState(null);
        }

        // Now detect product-data states from the product JSON endpoint.
        // We fetch product JSON concurrently (it's a tiny endpoint) to avoid
        // needing to parse variants from the SRA HTML.
        fetchProductJson(productUrl, function (err, productJson) {
          if (!err && productJson) {
            var state = detectProductState(productJson);
            if (state === 'sold-out') {
              hydrateSoldOutStub(productJson);
              setDialogState('sold-out');
              // Focus the notify-me CTA.
              var notifyBtn = dialogEl.querySelector('[data-bv-bis-trigger]');
              if (notifyBtn) { try { notifyBtn.focus(); } catch (_e) { /* no-op */ } }
            } else if (state === 'no-size') {
              applyNoSizeState();
              // Focus first interactive after picker collapse.
              focusFirstInteractive(contentEl);
            } else {
              // Default state — focus first interactive in content area.
              focusFirstInteractive(contentEl);
            }
          } else {
            // Product JSON fetch failed — non-fatal; default to displaying
            // whatever the SRA projected.
            focusFirstInteractive(contentEl);
          }
        });
      })
      .catch(function (_err) {
        fetchInFlight = false;
        retryCount++;

        if (!isOpen) {
          // First open failure — show dialog in network-error state.
          if (typeof dialogEl.showModal === 'function') {
            dialogEl.showModal();
            isOpen = true;
          } else {
            // Old browser — fall back to PDP navigation.
            fallbackNavigate(productUrl);
            return;
          }
        }
        showNetworkError(productUrl);
      });
  }

  function openQuickView(productUrl, sourcePage) {
    ensureSingleton();
    if (!dialogEl || !contentEl) {
      fallbackNavigate(productUrl);
      return;
    }
    if (!productUrl) return;

    currentProductUrl = productUrl;
    retryCount = 0;

    // Cache productUrl on the slot for the "view full details" anchor.
    contentEl.setAttribute('data-bv-quick-view-product-url', productUrl);

    performFetch(productUrl, false);
  }

  function retryFetch(productUrl) {
    if (fetchInFlight) return;
    // Clear the network-error display before retrying.
    setDialogState(null);
    if (dialogEl) {
      var fallback = dialogEl.querySelector('[data-bv-qv-pdp-fallback]');
      if (fallback) fallback.setAttribute('hidden', '');
    }
    performFetch(productUrl, true);
  }

  function closeQuickView() {
    if (dialogEl && typeof dialogEl.close === 'function') {
      dialogEl.close();
    }
  }

  function quickViewIsOpen() {
    return Boolean(isOpen);
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Delegation
  // ──────────────────────────────────────────────────────────────────────────

  function onTriggerClick(e) {
    if (e.defaultPrevented) return;
    if (e.button !== undefined && e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var trigger = e.target && e.target.closest
      ? e.target.closest(TRIGGER_SELECTOR)
      : null;
    if (!trigger) return;

    var productUrl = trigger.getAttribute('data-bv-quick-view-product-url');
    if (!productUrl) return;

    e.preventDefault();
    lastTriggerEl = trigger;
    var sourcePage = trigger.getAttribute('data-bv-source-page') || 'plp';
    openQuickView(productUrl, sourcePage);
  }

  function init() {
    ensureSingleton();
    document.addEventListener('click', onTriggerClick);

    window.benvel = window.benvel || {};
    window.benvel.quickView = {
      open: openQuickView,
      close: closeQuickView,
      isOpen: quickViewIsOpen,
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
